import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  FileBarChart2,
  BookOpen,
  Users,
  AlertTriangle,
  Upload,
  Database,
  LogOut,
  ShieldCheck,
  Zap,
  Plug,
  LayoutGrid,
  Layers,
  Inbox as InboxIcon,
  FileText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { to: "/dashboard", label: "Executive", icon: LayoutDashboard, roles: ["SUPER_ADMIN", "EXEC", "SITE_MANAGER"] },
  { to: "/companies", label: "Companies", icon: Building2, roles: ["SUPER_ADMIN", "EXEC", "SITE_MANAGER"] },
  { to: "/my-dashboards", label: "My Dashboards", icon: LayoutGrid, roles: ["SUPER_ADMIN", "EXEC", "SITE_MANAGER"] },
  { to: "/workspace", label: "Workspace", icon: Layers, roles: ["SUPER_ADMIN", "EXEC", "SITE_MANAGER"] },
  { to: "/reports", label: "Reports", icon: FileText, roles: ["SUPER_ADMIN", "EXEC", "SITE_MANAGER"] },
  { to: "/inbox", label: "Inbox", icon: InboxIcon, roles: ["SUPER_ADMIN", "EXEC", "SITE_MANAGER"] },
  { to: "/records", label: "Records", icon: Database, roles: ["SUPER_ADMIN", "EXEC", "SITE_MANAGER"] },
  { to: "/fines", label: "Fines", icon: AlertTriangle, roles: ["SUPER_ADMIN", "EXEC", "SITE_MANAGER"] },
  { to: "/vc-reporting", label: "VC Reporting", icon: FileBarChart2, roles: ["SUPER_ADMIN", "EXEC"] },
  { to: "/upload", label: "Ingest Data", icon: Upload, roles: ["SUPER_ADMIN", "EXEC"] },
  { to: "/etl", label: "ETL Jobs", icon: Zap, roles: ["SUPER_ADMIN"] },
  { to: "/connectors", label: "Connectors", icon: Plug, roles: ["SUPER_ADMIN"] },
  { to: "/data-dictionary", label: "Data Dictionary", icon: BookOpen, roles: ["SUPER_ADMIN"] },
  { to: "/users", label: "Users & Roles", icon: Users, roles: ["SUPER_ADMIN"] },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <aside
      className="hidden h-screen w-64 shrink-0 flex-col border-r border-navy-500/40 bg-navy-600 text-slate-300 lg:flex"
      data-testid="sidebar"
    >
      {/* Brand */}
      <div className="flex h-16 items-center gap-3 border-b border-navy-500/50 px-6">
        <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-brand-orange text-white">
          <ShieldCheck className="h-4 w-4" />
        </div>
        <div className="leading-tight">
          <div className="font-display text-sm font-semibold tracking-wider text-white">
            HONEY BADGER
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
            TCP Analytical
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="px-4 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          Workspace
        </div>
        {navItems
          .filter((it) => !it.roles || it.roles.includes(user?.role))
          .map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/dashboard"}
              data-testid={`nav-${to.replace(/\//g, "")}`}
              className={({ isActive }) =>
                [
                  "group relative mx-2 flex items-center gap-3 rounded-sm px-4 py-2.5 text-sm transition-colors",
                  isActive
                    ? "sidebar-active bg-brand-orange/10 text-white"
                    : "text-slate-400 hover:bg-navy-500/40 hover:text-white",
                ].join(" ")
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
      </nav>

      {/* User card */}
      <div className="border-t border-navy-500/50 p-4">
        <div className="mb-3 flex items-center gap-3 rounded-sm bg-navy-700/70 px-3 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-brand-orange text-white">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-full w-full rounded-sm object-cover" />
            ) : (
              <span className="font-display text-sm font-semibold">
                {user?.name?.[0]?.toUpperCase() || "?"}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-white" data-testid="sidebar-user-name">
              {user?.name}
            </div>
            <div className="truncate text-[10px] uppercase tracking-widest text-slate-400">
              {user?.role}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onLogout}
          data-testid="sidebar-logout-button"
          className="flex w-full items-center gap-2 rounded-sm border border-navy-500/60 px-3 py-2 text-xs text-slate-300 transition-colors hover:border-brand-orange hover:text-white"
        >
          <LogOut className="h-3.5 w-3.5" />
          Sign out
        </button>
      </div>
    </aside>
  );
}
